import { Inject, Injectable } from '@angular/core';
import { TransitionService } from '@uirouter/angular';
import { IFBWindow } from '@app/models/window-namespace';
import { DOCUMENT } from '@angular/common';

/**
 * Enables scroll to element with the value of scrollToId search parameter.
 * E.g. navigating to a page with url `page?scrollToId=heading` will open the page
 * and then scroll element with id `heading` into a view.
 */
@Injectable({
  providedIn: 'root'
})
export class ScrollToIdService {
  private whenComplete: number;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject('Window') private window: IFBWindow,
    private $transitions: TransitionService
  ) {}

  private get href(): string {
    return this.window.location.href;
  }

  listenTranslationSuccess(): void {
    this.$transitions.onSuccess({}, () => {
      this.scrollToId();
    });
  }

  scrollToId(): void {
    const matches = this.href.match(/scrollToId=([^&]*)/);

    if (!matches || matches.length < 2) {
      return;
    }

    const scrollToId = matches[1];

    if (!scrollToId) {
      return;
    }

    // There is an interval as rendering is triggered asynchronously,
    // and it's hard to predict when the certain element will be visible.
    this.whenComplete = this.window.setInterval(() => {
      // If it is an id generated by the table of contents component (has `toc${i}.` prefix),
      // instead of matching a full id, match only the prefix.
      // It's due to table of contents component generates different ids for different languages,
      // e.g. `toc1.Main-Heading` vs `toc1.Hauptüberschrift`
      const matches = scrollToId.match(/^toc\d+./);
      const selector = matches ? `app-table-of-contents [id^="${matches[0]}"]` : `#${scrollToId}`;
      const elem = this.document.querySelector(selector);

      if (elem) {
        elem.scrollIntoView();
        // Element found – stop the interval
        this.stopInterval();
      }
    }, 100);
  }

  private stopInterval(): void {
    if (this.whenComplete) {
      this.window.clearInterval(this.whenComplete);
    }
  }
}
